import { useContext, useEffect, useState } from 'react';
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import axios from "axios";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import AuthContext from "../../../contexts/AuthContext";
import "./SalesBulkMessages.css";
import showNotification from "../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import SalesBulkMessagesPreview from "./SalesBulkMessagesPreview/SalesBulkMessagesPreview";
// import SalesBulkMessagesForm from "./SalesBulkMessagesForm/SalesBulkMessagesForm";
import { Messages } from './Messages';
import moment from 'moment/moment';
import { LEADS_CAMPAIGN } from '../../../utils/leads-constant';

// 'send': envía por los bots y además carga la interacción en los leads.
// 'register': no envía nada, solo carga la interacción (mensajes mandados a mano).
export const BULK_MODES = { SEND: 'send', REGISTER: 'register' };
const DEFAULT_CAMPAIGN = 'Recontacto';

const INITIAL_FORM_VALUES = {
    mode: BULK_MODES.SEND,
    seller_id: '',
    campaign: LEADS_CAMPAIGN.includes(DEFAULT_CAMPAIGN) ? DEFAULT_CAMPAIGN : LEADS_CAMPAIGN[0],
    academic_units: [],
    time: '',
    date: new Date(),
    phones: [],
    mediaData: null,
    message: null,
    messageType: null,
    batch_size: 50,
    api_instances: [],
    interval_range: {
        min_ms: 1500,
        max_ms: 5000,
    },
    pause: 3
}

const isRegisterMode = (formValues) => formValues?.mode === BULK_MODES.REGISTER;

// Las validaciones de bots, tiempos y programación solo aplican al envío real
const onlySend = (validator) => (value, formValues) =>
    isRegisterMode(formValues) ? null : validator(value, formValues);

const validation = {
    phones: (value) => {
        if (!value || value?.length === 0) return 'Debes incluir al menos un número telefónico.'
    },
    seller_id: (value) => {
        if (!value) return 'Selecciona el vendedor que realizó el contacto.'
    },
    campaign: (value) => {
        if (!value) return 'Selecciona la forma de contacto.'
    },
    academic_units: (value, formValues) => {
        if (isRegisterMode(formValues) && (!value || value.length === 0)) return 'Selecciona al menos una unidad académica.'
    },
    batch_size: onlySend((value) => {
        if (!value) return 'Ingresa un número que indique la cantidad del bloque de mensajes.'
        if (value > 100) return 'El bloque debe ser un número entre 20 a 100.'
        if (value < 20) return 'El bloque debe ser un número entre 20 a 100.'
    }),
    api_instances: onlySend((value) => {
        if (!value || value?.length === 0) return 'Debes incluir al menos un bot.'
    }),
    'interval_range.min_ms': onlySend((value) => {
        if (!value) return 'Ingresa un número que indique el tiempo mínimo entre mensajes.'
        if (value > 18000) return 'El mínimo debe ser un número entre 1500 a 18000.'
        if (value < 1500) return 'El mínimo debe ser un número entre 1500 a 18000.'
    }),
    'interval_range.max_ms': onlySend((value, formValues) => {
        if (!value) return 'Ingresa un número que indique el tiempo máximo entre mensajes.'
        if (value > 30000) return 'El máximo debe ser un número entre 4000 a 30000.'
        if (value < 4000) return 'El máximo debe ser un número entre 4000 a 30000.'

        // 👇 Validación adicional
        if (formValues?.interval_range?.min_ms && value <= formValues.interval_range.min_ms) {
            return 'El máximo debe ser mayor que el mínimo.'
        }
    }),
    pause: onlySend((value) => {
        if (!value) return 'Ingresa un número que indique el tiempo de pausa entre cada bloque.'
        if (value > 10) return 'La pausa debe ser número entre 3 a 10.'
        if (value < 3) return 'La pausa debe ser número entre 3 a 10.'
    }),
    date: onlySend((value, formValues) => {
        const MAX_DAY_LIMIT = 7;

        const selectedDate = moment(value, 'DD/MM/YYYY', true); // validación estricta
        const today = moment().startOf('day');
        const maxLimit = moment().add(MAX_DAY_LIMIT, 'days').endOf('day');

        if (!selectedDate.isValid()) {
            return 'La fecha seleccionada no es válida.';
        }

        if (selectedDate.isBefore(today)) {
            return 'La fecha debe ser hoy o una fecha futura.';
        }

        if (selectedDate.isAfter(maxLimit)) {
            return `La fecha no puede superar los ${MAX_DAY_LIMIT} días desde hoy.`;
        }

        if (formValues?.time) {
            const [hours, minutes] = formValues.time.split(':');

            // 👇 usar clone() para evitar mutar el original
            const combined = selectedDate.clone().set({
                hour: Number(hours),
                minute: Number(minutes),
                second: 0,
            });


            const now = moment();


            // 👇 validación de fecha y hora combinada
            if (combined.isBefore(now)) {
                return 'La fecha y hora deben ser futuras.';
            }
        }

        return null; // <-- importante para que Mantine no asuma error
    }),
    time: onlySend((value) => {
        // Si querés validar formato, podés agregar algo como esto:
        if (value && !/^\d{2}:\d{2}$/.test(value)) {
            return 'Formato de hora inválido.';
        }
        return false;
    }),
}


function SalesBulkMessages() {
    const { authToken } = useContext(AuthContext);

    const [campaignsData, setCampaignsData] = useState([]);
    // In this case, initial values will be used to replicate a campaign, not for editing
    const [initialCampaignValues, setInitialCampaignValues] = useState(null);
    const [displayForm, setDisplayForm] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [loading, setLoading] = useState(false);

    const axiosConfig = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
        }
    };

    const createCampaign = async (values, config) => {
        const message = values.message !== null ? values.message : values.mediaData

        let start_at = null;

        if (values.date && values.time) {
            const [hours, minutes] = values.time.split(':');

            start_at = moment(values.date, 'DD/MM/YYYY')
                .set({ hour: Number(hours), minute: Number(minutes), second: 0 })
                .toISOString(); // genera UTC
        }

        const jsonData = {
            message,
            phones: values.phones,
            config: {
                message_type: values.mediaData ? 'media' : 'text',
                batch_size: values.batch_size,
                min_ms: values.interval_range.min_ms,
                max_ms: values.interval_range.max_ms,
                pause: values.pause,
                api_instances: values.api_instances,
                start_at,
            },
            // Para cargar la interacción en cada lead
            contactAttempt: {
                seller_id: values.seller_id,
                campaign: values.campaign,
            },
        };

        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/createMessageCampaign`,
            { data: jsonData },
            config
        );
    };

    // Envío simulado: no manda mensajes, solo carga el contactAttempt en los leads
    const registerContactAttempts = async (values, config) => {
        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/registerContactAttempts`,
            {
                data: {
                    phones: values.phones,
                    academicUnits: values.academic_units,
                    seller_id: values.seller_id,
                    campaign: values.campaign,
                },
            },
            config
        );
    };

    const leadsSummary = (leads) => {
        if (!leads) return '';
        const parts = [
            `${leads.updated} lead(s) actualizados`,
            `${leads.created} lead(s) nuevos`,
        ];
        if (leads.invalid?.length) parts.push(`${leads.invalid.length} número(s) inválidos: ${leads.invalid.join(', ')}`);
        return parts.join(' · ');
    };

    const handleRegister = async (values) => {
        try {
            setLoading(true);
            const { data } = await registerContactAttempts(values, axiosConfig);

            showNotification({
                color: "green",
                status: "success",
                title: "Interacciones registradas.",
                message: leadsSummary(data),
                autoClose: data?.invalid?.length ? 12000 : 6000,
            });
            setUpdateData(!updateData);
            setDisplayForm(false);
        } catch (err) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudieron registrar las interacciones. Detalle: ${err.response?.data?.message || err.message || ERROR_HANDLER[err.request?.status] || ERROR_HANDLER.defaultError}`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [

    ];

    // TABLE FUNCTIONS
    const handleSubmit = async (values) => {
        if (values.mode === BULK_MODES.REGISTER) return handleRegister(values);

        if (!values.api_instances || values.api_instances?.length === 0) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No se han encontrado bots para realizar los mensajes.`,
            });
        }

        if (!values.phones || values.phones?.length === 0) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No se han encontrado números de telefónicos.`,
            });
        }

        if (!values?.message && !values?.mediaData) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No hay mensaje confirmado a utilizar.`,
            });
        }

        try {
            setLoading(true);
            const { data } = await createCampaign(values, axiosConfig);

            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: `La campaña ha sido creada / iniciada con éxito. ${leadsSummary(data?.leads)}`,
            });
            if (data?.leadsError) {
                showNotification({
                    color: "orange",
                    status: "error",
                    title: "Los mensajes se enviaron, pero no se cargaron en los leads.",
                    message: `Detalle: ${data.leadsError}`,
                    autoClose: 12000,
                });
            }
            setUpdateData(!updateData);
            setDisplayForm(false);
        } catch (err) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / iniciar la campaña. Detalle: ${err.message || ERROR_HANDLER[err.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
            setCampaignsData(null);
        }
    };

    // DATA INITIALIZATION
    const init = async () => {
        // Get campaigns
        // const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/campaigns`;
        // const campaignsInfo = await axios.get(url, axiosConfig);

        setCampaignsData([]);
    };

    useEffect(() => {
        init();
    }, [updateData]);

    return (
        <div className="salesBulkMessages__container">
            {displayForm && (
                <ContentCreator
                    title={"Crear campaña"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialCampaignValues(null);
                    }}
                    validation={validation}
                    initialValues={initialCampaignValues ?? INITIAL_FORM_VALUES}
                    renderPreview={(_form) => (
                        <SalesBulkMessagesPreview form={_form.form} />
                    )}
                    renderForm={(_form) => (
                        // <SalesBulkMessagesForm initialValues={campaignsData} loading={loading} form={_form.form}/>
                        <Messages form={_form.form} loading={loading} />
                    )}
                />
            )}
            {campaignsData && (
                <TanStackTable
                    data={campaignsData}
                    columns={columns}
                    // editFunc={handleEditCampaign}
                    displayForm={displayForm}
                    addButtonFunc={() => {
                        setDisplayForm(!displayForm);
                        if (displayForm) setInitialCampaignValues(null);
                    }}
                />
            )}
        </div>
    );
}

export default SalesBulkMessages;
